// Static NANP (North American Numbering Plan) area-code → geography map, used to
// group +1 caller IDs by US state, fold Canada into one bucket, and split the
// sovereign Caribbean/Atlantic +1 nations out from the US catch-all. Sourced from
// the public NANPA assignment list (nationalnanpa.com). An unlisted code degrades
// to a generic "United States · other" bucket, so staleness is coarse rather than
// wrong — but it disagrees with the geography the dialer acts on, which is why
// nanp-area-codes.test.ts holds this map to `Hermes.Phone.Nanp`.

export interface NanpEntry {
  country: 'US' | 'CA';
  // Present only for US entries (states + territories). Canadian codes carry no
  // subdivision — Canada is a single region.
  state?: string;
  name?: string;
}

// US states/territories: [2-letter code, full name, [area codes]]. Territories
// (PR, VI, GU, AS, MP) are included so they resolve to their own 🇺🇸 bucket rather
// than the "other" catch-all.
const US_AREAS: ReadonlyArray<readonly [string, string, readonly number[]]> = [
  ['AL', 'Alabama', [205, 251, 256, 334, 483, 659, 938]],
  ['AK', 'Alaska', [907]],
  ['AZ', 'Arizona', [480, 520, 602, 623, 928]],
  ['AR', 'Arkansas', [327, 479, 501, 870]],
  [
    'CA',
    'California',
    [
      209, 213, 279, 310, 323, 341, 350, 357, 408, 415, 424, 442, 510, 530, 559, 562, 619, 626, 628,
      650, 657, 661, 669, 707, 714, 738, 747, 760, 805, 818, 820, 831, 837, 840, 858, 909, 916, 925,
      949, 951,
    ],
  ],
  ['CO', 'Colorado', [303, 719, 720, 748, 970, 983]],
  ['CT', 'Connecticut', [203, 475, 860, 959]],
  ['DE', 'Delaware', [302]],
  ['DC', 'Washington, DC', [202, 771]],
  [
    'FL',
    'Florida',
    [
      239, 305, 321, 324, 352, 386, 407, 448, 561, 645, 656, 689, 727, 728, 754, 772, 786, 813, 850,
      863, 904, 941, 954,
    ],
  ],
  ['GA', 'Georgia', [229, 404, 470, 478, 678, 706, 762, 770, 912, 943]],
  ['HI', 'Hawaii', [808]],
  ['ID', 'Idaho', [208, 986]],
  [
    'IL',
    'Illinois',
    [217, 224, 309, 312, 331, 447, 464, 618, 630, 708, 730, 773, 779, 815, 847, 861, 872],
  ],
  ['IN', 'Indiana', [219, 260, 317, 463, 574, 765, 812, 930]],
  ['IA', 'Iowa', [319, 515, 563, 641, 712]],
  ['KS', 'Kansas', [316, 620, 785, 913]],
  ['KY', 'Kentucky', [270, 364, 502, 606, 859]],
  ['LA', 'Louisiana', [225, 318, 337, 457, 504, 985]],
  ['ME', 'Maine', [207]],
  ['MD', 'Maryland', [227, 240, 301, 410, 443, 667]],
  ['MA', 'Massachusetts', [339, 351, 413, 508, 617, 774, 781, 857, 978]],
  ['MI', 'Michigan', [231, 248, 269, 313, 517, 586, 616, 679, 734, 810, 906, 947, 989]],
  ['MN', 'Minnesota', [218, 320, 507, 612, 651, 763, 924, 952]],
  ['MS', 'Mississippi', [228, 471, 601, 662, 769]],
  ['MO', 'Missouri', [235, 314, 417, 557, 573, 636, 660, 816, 975]],
  ['MT', 'Montana', [406]],
  ['NE', 'Nebraska', [308, 402, 531]],
  ['NV', 'Nevada', [702, 725, 775]],
  ['NH', 'New Hampshire', [603]],
  ['NJ', 'New Jersey', [201, 551, 609, 640, 732, 848, 856, 862, 908, 973]],
  ['NM', 'New Mexico', [505, 575]],
  [
    'NY',
    'New York',
    [
      212, 315, 329, 332, 347, 363, 465, 516, 518, 585, 607, 624, 631, 646, 680, 716, 718, 838, 845,
      914, 917, 929, 934,
    ],
  ],
  ['NC', 'North Carolina', [252, 336, 472, 704, 743, 828, 910, 919, 980, 984]],
  ['ND', 'North Dakota', [701]],
  ['OH', 'Ohio', [216, 220, 234, 283, 326, 330, 380, 419, 436, 440, 513, 567, 614, 740, 937]],
  ['OK', 'Oklahoma', [405, 539, 572, 580, 918]],
  ['OR', 'Oregon', [458, 503, 541, 971]],
  [
    'PA',
    'Pennsylvania',
    [215, 223, 267, 272, 412, 445, 484, 570, 582, 610, 717, 724, 814, 835, 878],
  ],
  ['RI', 'Rhode Island', [401]],
  ['SC', 'South Carolina', [803, 821, 839, 843, 854, 864]],
  ['SD', 'South Dakota', [605]],
  ['TN', 'Tennessee', [423, 615, 629, 729, 731, 865, 901, 931]],
  [
    'TX',
    'Texas',
    [
      210, 214, 254, 281, 325, 346, 361, 409, 430, 432, 469, 512, 621, 682, 713, 726, 737, 806, 817,
      830, 832, 903, 915, 936, 940, 945, 956, 972, 979,
    ],
  ],
  ['UT', 'Utah', [385, 435, 801]],
  ['VT', 'Vermont', [802]],
  ['VA', 'Virginia', [276, 434, 540, 571, 686, 703, 757, 804, 826, 948]],
  ['WA', 'Washington', [206, 253, 360, 425, 509, 564]],
  ['WV', 'West Virginia', [304, 681]],
  ['WI', 'Wisconsin', [262, 274, 353, 414, 534, 608, 715, 920]],
  ['WY', 'Wyoming', [307]],
  ['PR', 'Puerto Rico', [787, 939]],
  ['VI', 'US Virgin Islands', [340]],
  ['GU', 'Guam', [671]],
  ['AS', 'American Samoa', [684]],
  ['MP', 'Northern Mariana Islands', [670]],
];

// Canadian area codes (a +1 number with one of these is folded into a single
// "Canada" region rather than a US state).
const CA_CODES: readonly number[] = [
  204, 226, 236, 249, 250, 257, 263, 289, 306, 343, 354, 365, 367, 368, 382, 403, 416, 418, 428,
  431, 437, 438, 450, 468, 474, 506, 514, 519, 548, 579, 581, 584, 587, 604, 613, 639, 647, 672,
  683, 705, 709, 742, 753, 778, 780, 782, 807, 819, 825, 867, 873, 879, 902, 905,
];

export const NANP_AREA_CODES: Record<string, NanpEntry> = (() => {
  const map: Record<string, NanpEntry> = {};
  for (const [state, name, codes] of US_AREAS) {
    for (const code of codes) map[String(code)] = { country: 'US', state, name };
  }
  for (const code of CA_CODES) map[String(code)] = { country: 'CA' };
  return map;
})();

// A NANP region that is neither the US nor Canada — a distinct country that
// shares country code +1 (the Caribbean/Atlantic nations). `key`/`label`/`flag`
// mirror CallerRegion structurally, so board-state can return them as-is without
// importing back into this data module. US territories (PR/VI/GU/AS/MP) are NOT
// here — they belong to their own 🇺🇸 buckets in US_AREAS above.
export interface NanpCountry {
  key: string;
  label: string;
  flag: string;
}

// [key, label, flag, [area codes]]. Sourced from the NANPA SPN (Special Number
// Plan) assignments — the sovereign +1 nations Twilio can issue numbers in.
const OTHER_NANP: ReadonlyArray<readonly [string, string, string, readonly number[]]> = [
  ['BS', 'Bahamas', '🇧🇸', [242]],
  ['BB', 'Barbados', '🇧🇧', [246]],
  ['AI', 'Anguilla', '🇦🇮', [264]],
  ['AG', 'Antigua & Barbuda', '🇦🇬', [268]],
  ['VG', 'British Virgin Islands', '🇻🇬', [284]],
  ['KY', 'Cayman Islands', '🇰🇾', [345]],
  ['BM', 'Bermuda', '🇧🇲', [441]],
  ['GD', 'Grenada', '🇬🇩', [473]],
  ['TC', 'Turks & Caicos', '🇹🇨', [649]],
  ['MS', 'Montserrat', '🇲🇸', [664]],
  ['SX', 'Sint Maarten', '🇸🇽', [721]],
  ['LC', 'Saint Lucia', '🇱🇨', [758]],
  ['DM', 'Dominica', '🇩🇲', [767]],
  ['VC', 'Saint Vincent & Grenadines', '🇻🇨', [784]],
  ['DO', 'Dominican Republic', '🇩🇴', [809, 829, 849]],
  ['TT', 'Trinidad & Tobago', '🇹🇹', [868]],
  ['KN', 'Saint Kitts & Nevis', '🇰🇳', [869]],
  ['JM', 'Jamaica', '🇯🇲', [658, 876]],
];

export const NANP_OTHER_COUNTRIES: Record<string, NanpCountry> = (() => {
  const map: Record<string, NanpCountry> = {};
  for (const [key, label, flag, codes] of OTHER_NANP) {
    for (const code of codes) map[String(code)] = { key, label, flag };
  }
  return map;
})();
